import React, { useCallback, useContext, useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Button, Spinner } from "reactstrap";
import { toast } from "react-toastify";
import { ProductContext } from "../../../store";
import DetailSkeleton from "../../../components/Skeleton/DetailSkeleton";
import SchemaOptimizerIconButton from "../../../components/AiVisibility/SchemaOptimizer/SchemaOptimizerIconButton";
import SchemaOptimizerStatusBadge, {
	SchemaOptimizerRunStatusBadge,
} from "../../../components/AiVisibility/SchemaOptimizer/SchemaOptimizerStatusBadge";
import {
	getSchemaOptimizerJob,
	getSchemaOptimizerRun,
	isSchemaOptimizerJobFailed,
	isSchemaOptimizerJobInFlight,
	isSchemaOptimizerJobSuccess,
	isSchemaOptimizerRunInFlight,
	regenerateSchemaOptimizerJob,
	SCHEMA_OPTIMIZER_JOB_POLL_MS,
} from "../../../services/schemaOptimizerApi";
import { jobSourceLabel } from "../../../components/AiVisibility/SchemaOptimizer/schemaOptimizerUtils";

const SOURCE_LABELS = {
	single: "Single URL",
	manual: "Page URLs",
	sitemap: "Sitemap",
	csv: "CSV",
};

function runJobUrlLabel(job) {
	const label = jobSourceLabel(job);
	if (label && String(label).trim()) return label;
	return job?.url ?? job?.page_url ?? job?.pageUrl ?? "—";
}

function runFromNavigationSeed(locationState, runIdParam) {
	const seedRunId = locationState?.schemaOptimizerRunId;
	const jobIds = locationState?.schemaOptimizerJobIds;
	if (!Array.isArray(jobIds) || jobIds.length === 0) return null;
	if (seedRunId != null && String(seedRunId) !== String(runIdParam)) return null;
	return {
		id: Number(runIdParam),
		source: locationState?.schemaOptimizerRunSource ?? "manual",
		status: "RUNNING",
		progressMessage: null,
		useBatchApi: locationState?.schemaOptimizerRunUseBatchApi ?? true,
		jobs: jobIds.map((id) => ({
			id,
			job_id: id,
			status: "PENDING",
			url: "",
		})),
	};
}

const AiVisibilitySchemaOptimizerRun = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const [searchParams] = useSearchParams();
	const runId = searchParams.get("runId");
	const { currentWebsiteId, userLoader, websiteLoader } = useContext(ProductContext);
	const [run, setRun] = useState(null);
	const [loading, setLoading] = useState(true);
	const [regeneratingId, setRegeneratingId] = useState(null);
	const [loadError, setLoadError] = useState(null);

	const load = useCallback(async () => {
		if (!currentWebsiteId || !runId) {
			setRun(null);
			setLoadError(null);
			setLoading(false);
			return;
		}
		const res = await getSchemaOptimizerRun(currentWebsiteId, runId);
		if (!res.ok) {
			const seed = runFromNavigationSeed(location.state, runId);
			if (seed) {
				setRun(seed);
				setLoadError(null);
				const details = await Promise.all(
					seed.jobs.map(async (row) => {
						const jobRes = await getSchemaOptimizerJob(currentWebsiteId, row.id);
						return jobRes.ok ? jobRes.job : row;
					})
				);
				setRun((prev) =>
					prev
						? {
								...prev,
								jobs: details,
								status: prev.status,
							}
						: prev
				);
			} else {
				setRun(null);
				setLoadError(res.message || "Could not load this run.");
			}
		} else {
			setRun(res.run);
			setLoadError(null);
		}
		setLoading(false);
	}, [currentWebsiteId, runId, location.state]);

	useEffect(() => {
		setLoading(true);
		load();
	}, [load]);

	useEffect(() => {
		if (!run || !currentWebsiteId) return undefined;
		const runBusy = isSchemaOptimizerRunInFlight(run.status);
		const jobsBusy = (run.jobs || []).some((j) => isSchemaOptimizerJobInFlight(j.status));
		if (!runBusy && !jobsBusy) return undefined;
		const timer = setInterval(load, SCHEMA_OPTIMIZER_JOB_POLL_MS);
		return () => clearInterval(timer);
	}, [run, currentWebsiteId, load]);

	async function handleRegenerate(job) {
		const id = job.id ?? job.job_id;
		if (id == null) return;
		setRegeneratingId(id);
		const res = await regenerateSchemaOptimizerJob(currentWebsiteId, id);
		setRegeneratingId(null);
		if (!res.ok) {
			toast.error(res.message || "Could not regenerate");
			return;
		}
		navigate(
			`/aivisibility/schema-optimizer/view?jobId=${encodeURIComponent(res.job.jobId)}`
		);
	}

	if (userLoader || websiteLoader) return <DetailSkeleton />;

	if (!runId) {
		return (
			<div className="contentBlock schema-optimizer">
				<p className="text-muted">No run selected.</p>
				<Button color="primary" onClick={() => navigate("/aivisibility/schema-optimizer")}>
					Back to Schema Optimizer
				</Button>
			</div>
		);
	}

	if (loading && !run) {
		return (
			<div className="contentBlock schema-optimizer text-center py-5">
				<Spinner />
			</div>
		);
	}

	if (!run) {
		return (
			<div className="contentBlock schema-optimizer">
				<p className="text-danger">{loadError || "Could not load this run."}</p>
				<Button color="link" className="p-0" onClick={() => navigate("/aivisibility/schema-optimizer")}>
					Back
				</Button>
			</div>
		);
	}

	const jobs = run.jobs || [];
	const source = SOURCE_LABELS[run.source] || run.source;
	const doneCount = jobs.filter((j) => isSchemaOptimizerJobSuccess(j.status)).length;
	const failedCount = jobs.filter((j) => isSchemaOptimizerJobFailed(j.status)).length;
	const useBatch =
		run.useBatchApi ?? run.use_batch_api ?? location.state?.schemaOptimizerRunUseBatchApi;
	const batchLabel =
		useBatch === true ? "OpenAI Batch API" : useBatch === false ? "Real-time" : null;
	const progressLine = run.progressMessage || run.progress_message;
	const statsLine =
		jobs.length > 0 ? `${doneCount} done, ${failedCount} failed` : null;
	const runSubtitle = [progressLine || statsLine, batchLabel].filter(Boolean).join(" · ");

	return (
		<div className="contentBlock ai-visibility-dashboard schema-optimizer schema-optimizer-run">
			<div className="schema-optimizer__run-bar">
				<button
					type="button"
					className="schema-optimizer__back-btn"
					onClick={() => navigate("/aivisibility/schema-optimizer")}
				>
					<i className="fas fa-arrow-left me-2" aria-hidden />
					Back
				</button>
				<div className="schema-optimizer__run-bar-main">
					<h1 className="schema-optimizer__run-title">
						{source} · {jobs.length} page{jobs.length === 1 ? "" : "s"}
					</h1>
					{runSubtitle ? (
						<p className="schema-optimizer__run-subtitle mb-0">{runSubtitle}</p>
					) : null}
				</div>
				<SchemaOptimizerRunStatusBadge status={run.status} />
			</div>

			<div className="schema-optimizer__run-table-wrap">
				<table className="schema-optimizer__run-table">
					<colgroup>
						<col className="schema-optimizer__run-col-url" />
						<col className="schema-optimizer__run-col-domain" />
						<col className="schema-optimizer__run-col-status" />
						<col className="schema-optimizer__run-col-actions" />
					</colgroup>
					<thead>
						<tr>
							<th scope="col">URL</th>
							<th scope="col">Domain</th>
							<th scope="col" className="schema-optimizer__run-table-center">Status</th>
							<th scope="col" className="schema-optimizer__run-table-end">Actions</th>
						</tr>
					</thead>
					<tbody>
						{jobs.map((job) => {
							const id = job.id ?? job.job_id;
							const label = runJobUrlLabel(job);
							const inFlight = isSchemaOptimizerJobInFlight(job.status);
							const isRegenerating = regeneratingId === id;
							return (
								<tr key={id ?? job.url ?? label}>
									<td className="schema-optimizer__run-table-url">
										<button
											type="button"
											className="schema-optimizer__run-url-link"
											onClick={() =>
												id != null &&
												navigate(
													`/aivisibility/schema-optimizer/view?jobId=${encodeURIComponent(id)}`
												)
											}
											disabled={id == null}
										>
											{label || "—"}
										</button>
										{job.error && isSchemaOptimizerJobFailed(job.status) && (
											<p className="schema-optimizer__run-row-error mb-0">{job.error}</p>
										)}
									</td>
									<td>{job.detectedDomain ?? job.detected_domain ?? "—"}</td>
									<td className="schema-optimizer__run-table-center">
										<div className="schema-optimizer__run-cell-center">
											<SchemaOptimizerStatusBadge status={job.status} />
										</div>
									</td>
									<td className="schema-optimizer__run-table-end">
										<div className="schema-optimizer__run-cell-end">
											{isRegenerating ? (
												<span className="schema-optimizer__run-action-spinner">
													<Spinner size="sm" />
												</span>
											) : (
												<SchemaOptimizerIconButton
													title="Regenerate job"
													iconClass="fas fa-redo"
													disabled={inFlight || id == null}
													onClick={() => handleRegenerate(job)}
												/>
											)}
										</div>
									</td>
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>
		</div>
	);
};

export default AiVisibilitySchemaOptimizerRun;
